import { useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { Search } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"

import { useAdminHostedAgentToolCatalogQuery } from "../hooks/queries/hosted-agents"

// Tool RECOMMENDATIONS for a skill package (#3074).
//
// Same catalog the agent wizard's Tools tab reads
// (`GET /admin/hosted-agents/tools/catalog`) so a package can only recommend a
// tool an admin can actually grant. What it is NOT is a permission editor: a
// package never widens anything by itself, and these names surface at install
// time as a diff the admin reviews. The copy says so, because "recommended
// tools" in a package editor reads like a grant if nothing corrects it.
//
// Two lists over one catalog: recommendations (what the skill works best with)
// and required tools (without which resolution drops the skill entirely).
// Requiring a tool implies recommending it — checking Required checks the
// other box too, rather than letting an admin ship a package that requires
// something it never asks to be granted.

export function SkillPackageToolPicker({
  recommended,
  required,
  onChange,
  disabled,
}: {
  recommended: string[]
  required: string[]
  onChange: (next: { recommended: string[]; required: string[] }) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const catalogQuery = useAdminHostedAgentToolCatalogQuery()

  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data])
  const knownNames = useMemo(() => new Set(catalog.map((tool) => tool.name)), [catalog])
  // A selection the catalog does not know (an older package, or a tool no
  // longer registered) stays visible so saving never silently drops it.
  const unknownSelected = useMemo(
    () => [...new Set([...recommended, ...required])].filter((name) => !knownNames.has(name)),
    [recommended, required, knownNames]
  )

  const rows = useMemo(() => {
    const all = [
      ...unknownSelected.map((name) => ({ name, description: "", group: "" })),
      ...catalog.map((tool) => ({
        name: tool.name,
        description: tool.description,
        group: tool.group,
      })),
    ]
    const query = search.trim().toLowerCase()
    if (!query) return all
    return all.filter(
      (tool) =>
        tool.name.toLowerCase().includes(query) || tool.description.toLowerCase().includes(query)
    )
  }, [catalog, unknownSelected, search])

  const toggleRecommended = (name: string, checked: boolean) => {
    onChange({
      recommended: checked ? [...recommended, name] : recommended.filter((entry) => entry !== name),
      // Un-recommending a tool cannot leave it required: a required tool the
      // package never asks to be granted is a package that is inert on every
      // agent that does not already have it.
      required: checked ? required : required.filter((entry) => entry !== name),
    })
  }

  const toggleRequired = (name: string, checked: boolean) => {
    onChange({
      recommended: checked && !recommended.includes(name) ? [...recommended, name] : recommended,
      required: checked ? [...required, name] : required.filter((entry) => entry !== name),
    })
  }

  return (
    <div className="space-y-3" data-testid="skill-package-tools">
      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.skillPackages.tools.help", {
          defaultValue:
            "What the skill suggests an agent should be granted. A package never grants anything by itself — an administrator reviews these as a diff when installing it on an agent. Required tools are the ones without which the skill is inert; the agent simply does not get the skill without them.",
        })}
      </p>

      <div className="relative">
        <Search
          aria-hidden
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2"
        />
        <Input
          className="pl-8"
          disabled={disabled}
          value={search}
          placeholder={t("adminDashboard.skillPackages.tools.search", {
            defaultValue: "Search tools",
          })}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {catalogQuery.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full" />
          ))}
        </div>
      ) : null}
      {catalogQuery.isError ? (
        <p className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm">
          {t("adminDashboard.skillPackages.tools.loadFailed", {
            defaultValue: "Could not load the tool catalog. Saved recommendations are unchanged.",
          })}
        </p>
      ) : null}

      <ul className="max-h-72 space-y-1 overflow-y-auto">
        {rows.map((tool) => {
          const isRecommended = recommended.includes(tool.name)
          const isRequired = required.includes(tool.name)
          return (
            <li
              key={tool.name}
              className="hover:bg-accent/50 flex flex-col gap-2 rounded-md border p-2 transition-colors sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="flex min-w-0 items-start gap-2">
                <Checkbox
                  checked={isRecommended}
                  disabled={disabled}
                  id={`skill-tool-${tool.name}`}
                  onCheckedChange={(checked) => toggleRecommended(tool.name, checked === true)}
                />
                <div className="min-w-0">
                  <Label className="font-mono text-xs" htmlFor={`skill-tool-${tool.name}`}>
                    {tool.name}
                  </Label>
                  {tool.description ? (
                    <p className="text-muted-foreground line-clamp-2 text-xs">{tool.description}</p>
                  ) : null}
                  {!knownNames.has(tool.name) && catalog.length > 0 ? (
                    <Badge className="mt-1" variant="outline">
                      {t("adminDashboard.skillPackages.tools.unknown", {
                        defaultValue: "Not in this platform's catalog",
                      })}
                    </Badge>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2 sm:pt-0.5">
                <Checkbox
                  checked={isRequired}
                  disabled={disabled}
                  id={`skill-tool-required-${tool.name}`}
                  onCheckedChange={(checked) => toggleRequired(tool.name, checked === true)}
                />
                <Label className="text-xs font-normal" htmlFor={`skill-tool-required-${tool.name}`}>
                  {t("adminDashboard.skillPackages.tools.required", { defaultValue: "Required" })}
                </Label>
              </div>
            </li>
          )
        })}
        {!catalogQuery.isLoading && rows.length === 0 ? (
          <li className="text-muted-foreground p-2 text-sm">
            {t("adminDashboard.skillPackages.tools.noMatches", {
              defaultValue: "No tool matches that search.",
            })}
          </li>
        ) : null}
      </ul>
    </div>
  )
}
